import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabase";
import { CAPABILITY } from "../../helpers/permissions";
import { loadScopedUsers } from "../../helpers/scopedQueries";
import { loadTaskCatalog } from "../../helpers/tasks/taskCatalog";
import {
  DESCRIPTION_MAX,
  featuresForLocation,
  findCatalogDuplicate,
  PRIORITY_OPTIONS,
  resolveTaskScope,
  TITLE_MAX,
  validateTaskInput,
} from "../../helpers/tasks/taskModel";
import {
  RecBar,
  RecField,
  RecIcon,
  useKeepFocusedFieldVisible,
  useKeyboardOpen,
} from "../materials/MaterialRecordParts";
import { durationToInput, parseDurationInput } from "../../helpers/tasks/taskDuration";
import { DurationInput } from "./TaskParts";
import { useWorkSchedule } from "../../hooks/useWorkSchedule";
import { computeExpectedCompletion, formatScheduleDate, SCHEDULE_STATUS } from "../../helpers/tasks/workSchedule";

import "./tasks.css";

/**
 * Read-only forecast under Priority | Duration. Recomputed from Start +
 * Duration + the org Work Schedule on every change; the Due date is only
 * compared against, never changed, and never used to compute it.
 */
export function ExpectedCompletionLine({ startDate, duration, dueDate, workSchedule }) {
  const parsed = parseDurationInput(duration, workSchedule?.minutesPerDay);
  const minutes = parsed.ok ? parsed.minutes : null;
  let body;
  let warn = "";
  if (!startDate && !minutes) {
    body = "Set a start date and a duration to see the expected completion.";
  } else if (!startDate) {
    body = "Set a start date to see the expected completion.";
  } else if (!minutes) {
    body = "Set a duration to see the expected completion.";
  } else if (!workSchedule || workSchedule.status === SCHEDULE_STATUS.UNKNOWN) {
    body = "Expected completion unavailable: the work schedule is not loaded.";
  } else {
    const iso = computeExpectedCompletion({ startDate, durationMinutes: minutes }, workSchedule.schedule);
    if (!iso) {
      body = "Expected completion unavailable.";
    } else {
      body = (
        <>
          Expected completion: <strong>{formatScheduleDate(iso, { withYear: iso.slice(0, 4) !== String(new Date().getFullYear()) })}</strong>
        </>
      );
      if (dueDate && iso > dueDate) warn = "This is after the due date.";
    }
  }
  return (
    <p className="tk-expected" data-testid="task-expected" aria-live="polite">
      {body}
      {warn ? <span className="tk-expected__warn">{warn}</span> : null}
    </p>
  );
}

const SUGGESTION_LIMIT = 5;

/**
 * New / Edit Project Task. Creating can start from the Task Catalog (the text
 * is COPIED; the task stays independent) or be typed as a one-off, with an
 * optional "Save to Task Catalog" (admins).
 */
export function TaskEditor({
  task,
  projectName,
  locations,
  defaultLocationId = "",
  canSaveToCatalog,
  actor,
  directReportIds,
  onCancel,
  onSubmit,
}) {
  const editing = Boolean(task);
  useKeyboardOpen(true);
  useKeepFocusedFieldVisible(true);

  const [title, setTitle] = useState(task?.title || "");
  const [description, setDescription] = useState(task?.description || "");
  const [locationId, setLocationId] = useState(task ? task.location_id || "" : defaultLocationId);
  const [featureId, setFeatureId] = useState(task?.feature_id || "");
  const [assigneeId, setAssigneeId] = useState(task?.assignee_id || "");
  const [priority, setPriority] = useState(task?.priority || "");
  const [dueDate, setDueDate] = useState(task?.due_date || "");
  const [startDate, setStartDate] = useState(task?.start_date || "");
  // Planned duration (minutes in the DB). Independent of Start/Due: changing
  // one never recalculates the other.
  const workSchedule = useWorkSchedule();
  const minutesPerDay = workSchedule.minutesPerDay;
  const [duration, setDuration] = useState(() => durationToInput(task?.planned_duration_minutes, minutesPerDay));
  const [catalogItem, setCatalogItem] = useState(null);
  const [saveToCatalog, setSaveToCatalog] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [browseAll, setBrowseAll] = useState(false);
  const [people, setPeople] = useState([]);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (editing) return undefined;
    let cancelled = false;
    loadTaskCatalog(actor, { includeArchived: false })
      .then((rows) => !cancelled && setCatalog(rows))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  useEffect(() => {
    let cancelled = false;
    loadScopedUsers(supabase, actor, CAPABILITY.TIMELOG_VIEW, "supabase_user_id, employee_name", {
      directReportIds,
    })
      .then((rows) => !cancelled && setPeople(rows || []))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needle = title.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (editing || catalogItem || !catalog.length) return [];
    const matches = needle
      ? catalog.filter((item) => item.title.toLowerCase().includes(needle))
      : catalog;
    return browseAll ? matches : matches.slice(0, SUGGESTION_LIMIT);
  }, [editing, catalogItem, catalog, needle, browseAll]);
  const totalMatches = useMemo(
    () =>
      catalog.filter((item) => !needle || item.title.toLowerCase().includes(needle)).length,
    [catalog, needle],
  );
  const alreadyInCatalog = !editing && !catalogItem && findCatalogDuplicate(catalog, title);

  const featureOptions = useMemo(() => {
    const list = featuresForLocation(locationId, locations);
    // Keep an archived feature the task already has so editing other fields
    // never silently clears it.
    if (
      task?.feature_id &&
      task.location_id === locationId &&
      !list.some((f) => f.project_feature_id === task.feature_id)
    ) {
      return [...list, { project_feature_id: task.feature_id, name: "Archived feature" }];
    }
    return list;
  }, [locationId, locations, task]);

  // Location drives Feature: clearing or changing the Location drops a Feature
  // that does not belong to the new one.
  function changeLocation(next) {
    const scope = resolveTaskScope(next, featureId, locations);
    setLocationId(scope.locationId);
    setFeatureId(scope.featureId);
  }

  function pick(item) {
    setCatalogItem(item);
    setTitle(item.title);
    setDescription(item.description || "");
    // A template carries a duration only (never dates).
    if (item.planned_duration_minutes) setDuration(durationToInput(item.planned_duration_minutes, minutesPerDay));
    setSaveToCatalog(false);
    setFieldErrors({});
  }

  function clearPick() {
    setCatalogItem(null);
  }

  async function handleSave() {
    if (saving) return;
    const parsed = parseDurationInput(duration, minutesPerDay);
    const checked = validateTaskInput({
      title,
      description,
      priority,
      dueDate,
      startDate,
      durationMinutes: parsed.ok ? parsed.minutes : null,
    });
    const errors = parsed.ok ? checked.errors : { ...checked.errors, duration: parsed.error };
    setFieldErrors(errors);
    setError("");
    if (!checked.ok || !parsed.ok) return;
    setSaving(true);
    try {
      await onSubmit({
        title,
        description,
        locationId,
        featureId: locationId && featureOptions.some((f) => f.project_feature_id === featureId) ? featureId : "",
        assigneeId,
        priority,
        dueDate,
        startDate,
        durationMinutes: parsed.minutes,
        catalogItem,
        saveToCatalog: !catalogItem && saveToCatalog && !alreadyInCatalog,
      });
    } catch (err) {
      setError(err?.message || "Unable to save the task.");
    } finally {
      setSaving(false);
    }
  }

  const peopleOptions = useMemo(() => {
    const list = [...people];
    if (actor?.supabase_user_id && !list.some((p) => p.supabase_user_id === actor.supabase_user_id)) {
      list.unshift({ supabase_user_id: actor.supabase_user_id, employee_name: actor.employee_name || "Me" });
    }
    if (assigneeId && !list.some((p) => p.supabase_user_id === assigneeId)) {
      list.push({ supabase_user_id: assigneeId, employee_name: "Current assignee" });
    }
    return list;
  }, [people, actor, assigneeId]);

  return (
    <div className="screen nx-screen rec-page rec-page--edit" data-testid="task-editor">
      <RecBar title={editing ? "Edit Task" : "New Task"} leading="close" onBack={onCancel} />

      <div className="rec-body">
        <section className="rec-card tk-section">
          <RecField label="Title" htmlFor="tk-title" required error={fieldErrors.title}>
            <input
              id="tk-title"
              className="rec-input"
              value={title}
              maxLength={TITLE_MAX + 20}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={editing ? "Task title" : "Type a task or pick from the catalog"}
              autoComplete="off"
            />
          </RecField>

          {catalogItem ? (
            <div className="tk-source" data-testid="task-from-catalog">
              <RecIcon name="task" />
              <span>
                From Task Catalog: <b>{catalogItem.title}</b>
              </span>
              <button type="button" className="tk-source__x" aria-label="Detach from catalog" onClick={clearPick}>
                <RecIcon name="close" />
              </button>
            </div>
          ) : null}

          {suggestions.length ? (
            <div className="tk-suggest" role="listbox" aria-label="Task Catalog suggestions" data-testid="catalog-suggestions">
              <div className="tk-suggest__label">From the Task Catalog</div>
              {suggestions.map((item) => (
                <button
                  key={item.task_catalog_id}
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="tk-suggest__item"
                  onClick={() => pick(item)}
                >
                  <span className="tk-suggest__title">{item.title}</span>
                  {item.description ? <span className="tk-suggest__desc">{item.description}</span> : null}
                </button>
              ))}
              {!browseAll && totalMatches > SUGGESTION_LIMIT ? (
                <button type="button" className="tk-suggest__more" onClick={() => setBrowseAll(true)}>
                  Show all {totalMatches}
                </button>
              ) : null}
            </div>
          ) : null}

          <RecField label="Description" htmlFor="tk-desc" error={fieldErrors.description}>
            <textarea
              id="tk-desc"
              className="rec-input rec-input--area"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Instructions or notes (optional)"
            />
            <div className="tk-counter">
              {description.length}/{DESCRIPTION_MAX}
            </div>
          </RecField>
        </section>

        <section className="rec-card tk-section">
          <div className="tl-grid2 tk-scope">
            <RecField label="Location" htmlFor="tk-location" icon="pin">
              <span className="rec-select">
                <select
                  id="tk-location"
                  className="rec-input"
                  value={locationId}
                  onChange={(e) => changeLocation(e.target.value)}
                >
                  <option value="">Whole project</option>
                  {locations.map((l) => (
                    <option key={l.project_location_id} value={l.project_location_id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </span>
            </RecField>
            <RecField label="Feature" htmlFor="tk-feature" icon="tag">
              <span className="rec-select">
                <select
                  id="tk-feature"
                  className="rec-input"
                  value={featureId}
                  disabled={!locationId || featureOptions.length === 0}
                  onChange={(e) => setFeatureId(e.target.value)}
                >
                  <option value="">
                    {!locationId ? "Pick a location" : featureOptions.length ? "No feature" : "No features"}
                  </option>
                  {featureOptions.map((f) => (
                    <option key={f.project_feature_id} value={f.project_feature_id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </span>
            </RecField>
          </div>

          {/* Row 1: Start | Due. Row 2: Priority | Duration. Row 3: Assignee (full width). */}
          <div className="tl-grid2 tk-schedule" data-testid="task-row-dates">
            <RecField label="Start Date" htmlFor="tk-start" icon="calendar" error={fieldErrors.startDate}>
              <input
                id="tk-start"
                type="date"
                className="rec-input tl-dt"
                value={startDate}
                max={dueDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </RecField>
            <RecField label="Due Date" htmlFor="tk-due" icon="calendar" error={fieldErrors.dueDate}>
              <input
                id="tk-due"
                type="date"
                className="rec-input tl-dt"
                value={dueDate}
                min={startDate || undefined}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </RecField>
          </div>

          <div className="tl-grid2 tk-schedule" data-testid="task-row-priority">
            <RecField label="Priority" htmlFor="tk-priority" icon="flag" error={fieldErrors.priority}>
              <span className="rec-select">
                <select
                  id="tk-priority"
                  className="rec-input"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {PRIORITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </span>
            </RecField>
            <RecField label="Duration" htmlFor="tk-duration" icon="clock" error={fieldErrors.duration}>
              <DurationInput
                id="tk-duration"
                amount={duration.amount}
                unit={duration.unit}
                minutesPerDay={minutesPerDay}
                onChange={setDuration}
              />
            </RecField>
          </div>
          <ExpectedCompletionLine
            startDate={startDate}
            duration={duration}
            dueDate={dueDate}
            workSchedule={workSchedule}
          />

          <RecField label="Assignee" htmlFor="tk-assignee" icon="user">
            <span className="rec-select">
              <select
                id="tk-assignee"
                className="rec-input"
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
              >
                <option value="">Unassigned</option>
                {peopleOptions.map((p) => (
                  <option key={p.supabase_user_id} value={p.supabase_user_id}>
                    {p.employee_name || "Unknown"}
                  </option>
                ))}
              </select>
            </span>
          </RecField>
        </section>

        {!editing && !catalogItem && canSaveToCatalog ? (
          <label className="rec-card rec-check tk-savecat">
            <input
              type="checkbox"
              checked={saveToCatalog && !alreadyInCatalog}
              disabled={Boolean(alreadyInCatalog)}
              onChange={(e) => setSaveToCatalog(e.target.checked)}
            />
            <span className="rec-check__box" aria-hidden="true">
              <RecIcon name="check" />
            </span>
            <span className="rec-check__text">
              <b>Save to Task Catalog</b>
              <small>
                {alreadyInCatalog
                  ? "A task with this title is already in the catalog. Pick it above to reuse it."
                  : "Make this reusable on other projects. Otherwise it stays a one-off."}
              </small>
            </span>
          </label>
        ) : null}

        {projectName ? <div className="tk-subtitle tk-subtitle--foot">Project: {projectName}</div> : null}

        {error ? (
          <div className="rec-banner" role="alert">
            {error}
          </div>
        ) : null}
      </div>

      <div className="rec-actions rec-actions--split">
        <button type="button" className="rec-btn rec-btn--ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="rec-btn rec-btn--primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : editing ? "Save Changes" : "Save Task"}
        </button>
      </div>
    </div>
  );
}
